
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import CatalogListGrid from "../../common/catalog/CatalogListGrid";
import CatalogEmptyState from "../../common/catalog/CatalogEmptyState";
import { getCatalogEmptyCopy } from "../../common/catalog/catalogEmptyCopy";
import Skeleton from "../../common/loading/Skeleton";
import Paginator from "../../common/paginator/Paginator";
import ClipCard from "./ClipCard";

const CLIPS_PER_PAGE = 12;

function clipMatchesExpertise(clip, expertiseId) {
	if (!expertiseId) {
		return true;
	}
	const idStr = String(expertiseId);
	if (Array.isArray(clip?.expertise_ids)) {
		if (clip.expertise_ids.some((id) => String(id) === idStr)) {
			return true;
		}
	}
	if (Array.isArray(clip?.expertises)) {
		return clip.expertises.some((item) => String(item?.id) === idStr);
	}
	return false;
}

export default function ClipSection({ isDesktop, query, expertiseId }) {
	const navigate = useNavigate();
	const [clips, setClips] = useState([]);
	const [page, setPage] = useState(1);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		setIsLoading(true);
		api
			.videoClipList({
				token: LoginHelper.getToken(),
				...(expertiseId ? { expertise_id: expertiseId } : {}),
			})
			.then((list) => {
				setClips(list ?? []);
				setIsLoading(false);
			})
			.catch(() => {
				setError("دریافت کلیپ‌ها ناموفق بود");
				setIsLoading(false);
			});
	}, [expertiseId]);

	useEffect(() => {
		setPage(1);
	}, [query, expertiseId]);

	const filtered = useMemo(() => {
		let list = clips;
		if (expertiseId) {
			list = list.filter((clip) => clipMatchesExpertise(clip, expertiseId));
		}
		const q = (query ?? "").trim().toLowerCase();
		if (!q) {
			return list;
		}
		return list.filter((c) => {
			const title = (c.title ?? "").toLowerCase();
			const description = (c.description ?? "").toLowerCase();
			return title.includes(q) || description.includes(q);
		});
	}, [clips, query, expertiseId]);

	if (isLoading) {
		return (
			<Skeleton variant="grid" type="videoclip" count={8} />
		);
	}

	const emptyCopy = getCatalogEmptyCopy({
		itemSingular: "چالش",
		query,
		hasExtraFilter: Boolean(expertiseId),
	});
	const maxPageNum = Math.ceil(filtered.length / CLIPS_PER_PAGE);
	const visibleClips = filtered.slice(
		(page - 1) * CLIPS_PER_PAGE,
		page * CLIPS_PER_PAGE,
	);

	return (
		<div>
			<CatalogListGrid isDesktop={isDesktop}>
				{visibleClips.map((clip) => (
					<ClipCard key={clip.id} clip={clip} />
				))}
			</CatalogListGrid>

			{maxPageNum > 1 && (
				<div className="mt-8 flex justify-center">
					<Paginator
						currentPage={page}
						maxPageNum={maxPageNum}
						isLoading={isLoading}
						onChange={setPage}
					/>
				</div>
			)}

			{filtered.length === 0 && !error && (
				<CatalogEmptyState
					message={emptyCopy.message}
					hint={emptyCopy.hint}
					actionLabel={
						emptyCopy.showClear ? "نمایش همهٔ چالش‌ها" : undefined
					}
					onAction={
						emptyCopy.showClear
							? () => navigate("/video_clips/")
							: undefined
					}
				/>
			)}

			{error && (
				<div className="mt-5 text-center font-Vazir-400 text-[#cc0000]">
					{error}
				</div>
			)}
		</div>
	);
}
